import React, { useState, useEffect } from "react";
import { Alert, Modal, Button } from "flowbite-react";
import { fetchTradeData, fetchDayDetails, auth } from "./firebase";
import { MonthView, DayDetails, AnnualCalendar, MonthlySummary } from "./components/Dashboard";

const months = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const Dashboard = ({ updateYearlySummary, refreshTrigger }) => {
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedDay, setSelectedDay] = useState(null);
  const [tradeData, setTradeData] = useState({});
  const [selectedDayDetails, setSelectedDayDetails] = useState(null);
  const [monthlySummary, setMonthlySummary] = useState({
    totalPL: 0,
    totalTrades: 0,
  });
  const [yearlySummary, setYearlySummary] = useState({
    totalPL: 0,
    totalTrades: 0,
  });
  const [user, setUser] = useState(null);
  const [error, setError] = useState(null);
  const [yearlyTradeData, setYearlyTradeData] = useState({});
  const [openModal, setOpenModal] = useState(false);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((currentUser) => {
      setUser(currentUser);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const loadTradeData = async () => {
      if (user) {
        try {
          const data = await fetchTradeData(user.uid, selectedYear, selectedMonth);
          setTradeData(data);
          calculateMonthlySummary(data);
        } catch (err) {
          console.error("Error loading trade data:", err);
          setError("Failed to load trade data");
        }
      }
    };
    loadTradeData();
  }, [user, selectedYear, selectedMonth, refreshTrigger]);

  useEffect(() => {
    const loadYearlyTradeData = async () => {
      if (user) {
        try {
          const yearlyData = {};
          for (let month = 0; month < 12; month++) {
            const data = await fetchTradeData(user.uid, selectedYear, month);
            yearlyData[month] = data || {};
          }
          setYearlyTradeData(yearlyData);
          const summary = calculateYearlySummary(yearlyData);
          setYearlySummary(summary);
          updateYearlySummary(summary); // Update the summary in App.js
          setTradeData(yearlyData[selectedMonth] || {});
          calculateMonthlySummary(yearlyData[selectedMonth] || {});
        } catch (err) {
          console.error("Error loading yearly trade data:", err);
          setError("Failed to load yearly trade data");
        }
      }
    };
    loadYearlyTradeData();
  }, [user, selectedYear, refreshTrigger, updateYearlySummary]);

  const calculateYearlySummary = (yearData) => {
    let yearlyTotalPL = 0;
    let yearlyTotalTrades = 0;
    Object.values(yearData).forEach((month) => {
      Object.values(month).forEach((day) => {
        if (day && typeof day.pl === "number" && typeof day.trades === "number") {
          yearlyTotalPL += day.pl;
          yearlyTotalTrades += day.trades;
        }
      });
    });
    return {
      totalPL: yearlyTotalPL,
      totalTrades: yearlyTotalTrades,
    };
  };

  const calculateMonthlySummary = (data) => {
    let totalPL = 0;
    let totalTrades = 0;
    Object.values(data).forEach((day) => {
      if (day && typeof day.pl === "number" && typeof day.trades === "number") {
        totalPL += day.pl;
        totalTrades += day.trades;
      }
    });
    setMonthlySummary({ totalPL, totalTrades });
  };

  const handleMonthSelect = (month) => {
    setSelectedMonth(month);
    setTradeData(yearlyTradeData[month] || {});
    calculateMonthlySummary(yearlyTradeData[month] || {});
    setSelectedDay(null);
    setSelectedDayDetails(null);
  };

  const handlePrevMonth = () => {
    if (selectedMonth > 0) {
      setSelectedMonth(selectedMonth - 1);
    } else {
      setSelectedYear(selectedYear - 1);
      setSelectedMonth(11);
    }
    setSelectedDay(null);
    setSelectedDayDetails(null);
  };

  const handleNextMonth = () => {
    if (selectedMonth < 11) {
      setSelectedMonth(selectedMonth + 1);
    } else {
      setSelectedYear(selectedYear + 1);
      setSelectedMonth(0);
    }
    setSelectedDay(null);
    setSelectedDayDetails(null);
  };

  const handlePrevYear = () => {
    setSelectedYear(selectedYear - 1);
    setSelectedDay(null);
    setSelectedDayDetails(null);
  };

  const handleNextYear = () => {
    setSelectedYear(selectedYear + 1);
    setSelectedDay(null);
    setSelectedDayDetails(null);
  };

  const handleDayClick = async (day) => {
    if (user) {
      try {
        setSelectedDay(day);
        const details = await fetchDayDetails(user.uid, selectedYear, selectedMonth, day);
        setSelectedDayDetails(details);

        // Open the modal on mobile devices
        if (window.innerWidth < 640) {
          setOpenModal(true);
        }
      } catch (err) {
        console.error("Error fetching day details:", err);
        setError("Failed to fetch day details");
      }
    }
  };

  const handleRefresh = async () => {
    if (user) {
      try {
        const updatedData = await fetchTradeData(user.uid, selectedYear, selectedMonth);
        setTradeData(updatedData);
        calculateMonthlySummary(updatedData);

        if (selectedDay) {
          const updatedDetails = await fetchDayDetails(user.uid, selectedYear, selectedMonth, selectedDay);
          setSelectedDayDetails(updatedDetails);
        }

        // Refresh yearly data
        const yearlyData = { ...yearlyTradeData };
        yearlyData[selectedMonth] = updatedData;
        setYearlyTradeData(yearlyData);
        const summary = calculateYearlySummary(yearlyData);
        setYearlySummary(summary);
        updateYearlySummary(summary);
      } catch (err) {
        console.error("Error refreshing data:", err);
        setError("Failed to refresh data");
      }
    }
  };

  return (
    <main className="flex-1 overflow-y-auto bg-slate-100 dark:bg-slate-900 lg:p-4">
      <div className="flex flex-col flex-grow overflow-hidden">
        {error && (
          <Alert color="failure" className="mb-2">
            <span className="font-medium">Info alert!</span>
            {error}
          </Alert>
        )}

        <div className="flex flex-grow overflow-hidden">
          <div className="flex-grow overflow-auto scrollbar">
            <MonthlySummary summary={monthlySummary} monthlyData={tradeData} />
            <MonthView
              year={selectedYear}
              month={selectedMonth}
              tradeData={tradeData}
              onDayClick={handleDayClick}
              selectedDay={selectedDay}
              onPrevMonth={handlePrevMonth}
              onNextMonth={handleNextMonth}
              onPrevYear={handlePrevYear}
              onNextYear={handleNextYear}
              months={months}
            />
            <AnnualCalendar year={selectedYear} tradeData={yearlyTradeData} onMonthSelect={handleMonthSelect} />
          </div>
          <div className="hidden sm:block h-screen w-1/3 pl-4 pr-1 flex-grow overflow-auto scrollbar">
            {selectedDayDetails && (
              <DayDetails
                details={selectedDayDetails}
                day={selectedDay}
                month={selectedMonth}
                months={months}
                year={selectedYear}
                userId={auth.currentUser.uid}
                onRefresh={handleRefresh}
              />
            )}
          </div>
        </div>
      </div>

      {/* Modal for mobile view */}
      <Modal show={openModal} onClose={() => setOpenModal(false)} position="center">
        <Modal.Header>
          {/* <h3 className="text-xl font-semibold">
            Day Summary:{selectedDay} {months[selectedMonth]} {selectedYear}
          </h3> */}
        </Modal.Header>
        <Modal.Body className="overflow-y-auto p-0">
          {selectedDayDetails && (
            <DayDetails
              details={selectedDayDetails}
              day={selectedDay}
              month={selectedMonth}
              months={months}
              year={selectedYear}
              userId={auth.currentUser.uid}
              onRefresh={handleRefresh}
            />
          )}
        </Modal.Body>
      </Modal>
    </main>
  );
};

export default Dashboard;